import { useMemo, useState } from "react";
import { formatTimestamp } from "../../lib/format-utils";
import { formatNZDCompact } from "@shared/nz-money";
import { cx } from "@shared/cx";
import { SectionCard } from "../ui/SectionCard";
import { Tooltip } from "../ui/Tooltip";
import forms from "../ui/field.module.css";
import badge from "../../lib/status-badge.module.css";
import styles from "./ContractorPaymentSection.module.css";
import { ConfirmReceivedForm } from "./ConfirmReceivedForm";
import { calculateAssignmentPay } from "../../lib/crew-payload";
import {
  MUTATION_KEYS,
  type MutationRunner,
} from "../../lib/use-event-workspace";
import type { WorkspaceAssignment } from "../../lib/event-workspace-api";
import { InlineBanner } from "../ui/InlineBanner";

/** The workspace assignment slice this section reads. */
type Assignment = Pick<
  WorkspaceAssignment,
  | "id"
  | "contractorId"
  | "contractor"
  | "status"
  | "hourlyRate"
  | "estimatedHours"
  | "tasksDescription"
  | "payAmount"
  | "paymentStatus"
  | "paymentConfirmedAt"
  | "jobsheetUrl"
  | "reminderSentAt"
>;

interface ContractorPaymentSectionProps {
  assignments: Assignment[];
  open: boolean;
  onToggle: () => void;
  sectionRef: React.RefObject<HTMLDivElement | null>;
  state: "active" | "complete" | "future";
  busy: boolean;
  mutations: MutationRunner;
  onConfirmPayment: (assignmentId: string, date: string, hours: number) => void;
}

export function ContractorPaymentSection({
  assignments,
  open,
  onToggle,
  sectionRef,
  state,
  busy,
  mutations,
  onConfirmPayment,
}: ContractorPaymentSectionProps) {
  const { accepted, paidCount } = useMemo(() => {
    const acc: Assignment[] = [];
    let paid = 0;
    for (const a of assignments) {
      if (a.status !== "accepted") continue;
      acc.push(a);
      if (a.paymentStatus === "paid") paid++;
    }
    return { accepted: acc, paidCount: paid };
  }, [assignments]);

  const [hoursWorked, setHoursWorked] = useState<Record<string, string>>({});

  const hoursValue = (assignment: Assignment): string =>
    hoursWorked[assignment.id] ?? assignment.estimatedHours?.toString() ?? "";
  const parsedHours = (assignment: Assignment): number | null => {
    const raw = hoursValue(assignment).trim();
    if (raw === "") return null;
    if (!/^\d+(?:\.\d{1,2})?$/.test(raw)) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 && value <= 999.99
      ? value
      : null;
  };
  const displayedPay = (assignment: Assignment): number => {
    if (assignment.paymentStatus === "paid") return assignment.payAmount ?? 0;
    const hours = parsedHours(assignment);
    return hours !== null && assignment.hourlyRate !== null
      ? calculateAssignmentPay(assignment.hourlyRate, hours)
      : (assignment.payAmount ?? 0);
  };
  const totalPay = accepted.reduce(
    (total, assignment) => total + displayedPay(assignment),
    0,
  );

  if (accepted.length === 0) return null;

  const summary =
    paidCount === accepted.length
      ? "All paid"
      : paidCount > 0
        ? `${paidCount}/${accepted.length} paid`
        : `${accepted.length} contractor${accepted.length !== 1 ? "s" : ""} to pay`;

  return (
    <SectionCard
      title="Contractor Payment"
      collapsible
      open={open}
      onToggle={onToggle}
      sectionRef={sectionRef}
      state={state}
      summary={summary}
    >
      <div className={styles.body}>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>Total contractor pay</span>
          <span className={styles.totalAmount}>
            {formatNZDCompact(totalPay)}
          </span>
        </div>

        <div className={styles.divider} />

        {accepted.map((a) => {
          const hours = parsedHours(a);
          const hoursValid = hours !== null;
          const rateValid = a.hourlyRate !== null && a.hourlyRate >= 0;
          return (
            <div key={a.id} className={styles.row}>
              <div className={styles.rowHead}>
                <div>
                  <span className={styles.name}>
                    {a.contractor?.name || "Unknown"}
                  </span>
                  {a.paymentStatus === "paid" ? (
                    <span
                      className={cx(badge.badge, badge.green, styles.badgeGap)}
                    >
                      Paid
                    </span>
                  ) : (
                    <span
                      className={cx(badge.badge, badge.yellow, styles.badgeGap)}
                    >
                      Unpaid
                    </span>
                  )}
                  {a.contractor?.bank_account && (
                    <span className={styles.bankAccount}>
                      <Tooltip content="The bank account on this Contractor's profile.">
                        {a.contractor.bank_account}
                      </Tooltip>
                    </span>
                  )}
                </div>
                <span className={styles.amount}>
                  {formatNZDCompact(displayedPay(a))}
                </span>
              </div>
              {a.paymentConfirmedAt ? (
                <div className={styles.settled}>
                  <p>
                    {a.estimatedHours ?? 0} hours at{" "}
                    {formatNZDCompact(a.hourlyRate ?? 0)}/hr
                  </p>
                  <p>Paid {formatTimestamp(a.paymentConfirmedAt)}</p>
                </div>
              ) : (
                <div className={styles.confirm}>
                  <div>
                    <label
                      className={forms.label}
                      htmlFor={`hours-worked-${a.id}`}
                    >
                      Hours worked
                      <span className={styles.rateHint}>
                        {rateValid
                          ? `${formatNZDCompact(a.hourlyRate ?? 0)}/hr`
                          : "Set an hourly rate in Crew first"}
                      </span>
                    </label>
                    <input
                      id={`hours-worked-${a.id}`}
                      type="number"
                      min="0"
                      max="999.99"
                      step="0.01"
                      className={forms.input}
                      value={hoursValue(a)}
                      onChange={(event) =>
                        setHoursWorked((previous) => ({
                          ...previous,
                          [a.id]: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <ConfirmReceivedForm
                    dateLabel="Date paid"
                    dateAriaLabel="Contractor pay date"
                    confirmLabel="Confirm Paid"
                    loading={mutations.isPending(
                      MUTATION_KEYS.confirmContractorPayment(a.id),
                    )}
                    disabled={busy || !hoursValid || !rateValid}
                    onConfirm={(date) =>
                      hours !== null && onConfirmPayment(a.id, date, hours)
                    }
                  />
                  {!hoursValid && hoursValue(a) !== "" && (
                    <InlineBanner
                      variant="error"
                      placement="none"
                      className={styles.spanBoth}
                    >
                      Enter hours from 0 to 999.99, using at most 2 decimal
                      places
                    </InlineBanner>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
